import { useCallback, useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../api';
import { VALID_SCOPES, SCOPE_HELP, type Scope } from '../config';
import type { ApiKey, CreatedKey } from '../types';
import { fmtDate, fmtRelative } from '../format';
import {
  Button,
  Chip,
  CopyButton,
  EnvBadge,
  Field,
  IconPlus,
  Modal,
  Spinner,
  useToast,
} from '../ui';

const EXPIRY_OPTIONS: { label: string; days: number | null }[] = [
  { label: 'Never', days: null },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '1 year', days: 365 },
];

export function KeysPage() {
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [loadError, setLoadError] = useState<string>();
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedKey | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoadError(undefined);
    try {
      const res = await apiFetch<{ ok: true; keys: ApiKey[] }>('/v1/keys');
      setKeys(res.keys);
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'Could not load keys.');
      setKeys([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>API keys</h1>
          <p className="sub">
            Keys authenticate your agents against the PrismTx API. They grant platform
            access only — moving funds always needs the paired phone.
          </p>
        </div>
        <Button variant="primary" onClick={() => setCreating(true)}>
          <IconPlus />
          New key
        </Button>
      </div>

      {keys === null ? (
        <div className="center">
          <Spinner />
        </div>
      ) : keys.length === 0 ? (
        <div className="card empty">
          <h3>No API keys yet</h3>
          <p>Create your first key to connect an agent.</p>
          {loadError && <p className="field__hint">{loadError}</p>}
        </div>
      ) : (
        <div className="keylist">
          {keys.map((k) => (
            <div className="card keyrow" key={k.id}>
              <div className="keyrow__main">
                <div className="keyrow__title">
                  <span className="keyrow__name">{k.name}</span>
                  <EnvBadge environment={k.environment} />
                  <span className="keyrow__id">
                    ptx_{k.environment === 'live' ? 'live' : 'test'}_{k.key_id}…
                  </span>
                </div>
                <div className="keyrow__scopes">
                  {k.scopes.length ? (
                    k.scopes.map((s) => <Chip key={s}>{s}</Chip>)
                  ) : (
                    <span className="field__hint">no scopes</span>
                  )}
                  {k.wallets && k.wallets.length > 0 && (
                    <Chip>{k.wallets.length} wallet{k.wallets.length > 1 ? 's' : ''}</Chip>
                  )}
                </div>
                <div className="keyrow__meta">
                  <span>
                    <b>Created</b> {fmtDate(k.created_at)}
                  </span>
                  <span>
                    <b>Last used</b> {fmtRelative(k.last_used_at)}
                  </span>
                  {k.expires_at && (
                    <span>
                      <b>Expires</b> {fmtDate(k.expires_at)}
                    </span>
                  )}
                </div>
              </div>
              <div className="keyrow__actions">
                <Button variant="danger" small onClick={() => setRevoking(k)}>
                  Revoke
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <CreateKeyModal
          onClose={() => setCreating(false)}
          onCreated={(c) => {
            setCreating(false);
            setCreated(c);
            void load();
          }}
        />
      )}

      {created && <RevealModal created={created} onClose={() => setCreated(null)} />}

      {revoking && (
        <RevokeModal
          k={revoking}
          onClose={() => setRevoking(null)}
          onRevoked={() => {
            toast.ok(`Revoked "${revoking.name}"`);
            setRevoking(null);
            void load();
          }}
          onError={(m) => toast.err(m)}
        />
      )}

      {toast.node}
    </>
  );
}

function CreateKeyModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (c: CreatedKey) => void;
}) {
  const [name, setName] = useState('');
  const [environment, setEnvironment] = useState<'live' | 'test'>('test');
  const [scopes, setScopes] = useState<Scope[]>(['portfolio']);
  const [wallets, setWallets] = useState('');
  const [expiryDays, setExpiryDays] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const toggleScope = (s: Scope) =>
    setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const canSubmit = name.trim().length > 0 && scopes.length > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(undefined);
    const walletList = wallets
      .split(/[\s,]+/)
      .map((w) => w.trim())
      .filter(Boolean);
    const expiresAt =
      expiryDays === null
        ? null
        : new Date(Date.now() + expiryDays * 86_400_000).toISOString();
    try {
      const c = await apiFetch<CreatedKey & { ok: true }>('/v1/keys', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          environment,
          scopes,
          wallets: walletList.length ? walletList : undefined,
          expiresAt,
        }),
      });
      onCreated(c);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not create the key.');
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Create API key"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {submitting ? 'Creating…' : 'Create key'}
          </Button>
        </>
      }
    >
      <Field label="Name" hint="A label to recognize this key — e.g. “trading-bot prod”.">
        <input
          className="input"
          value={name}
          autoFocus
          maxLength={80}
          placeholder="trading-bot prod"
          onChange={(e) => setName(e.target.value)}
        />
      </Field>

      <Field label="Environment">
        <select
          className="select"
          value={environment}
          onChange={(e) => setEnvironment(e.target.value as 'live' | 'test')}
        >
          <option value="test">Test</option>
          <option value="live">Live</option>
        </select>
      </Field>

      <Field label="Scopes" hint="What this key is allowed to do.">
        <div className="scopegrid">
          {VALID_SCOPES.map((s) => (
            <label key={s} className={`scopeopt${scopes.includes(s) ? ' scopeopt--on' : ''}`}>
              <input
                type="checkbox"
                checked={scopes.includes(s)}
                onChange={() => toggleScope(s)}
              />
              <span>
                <span className="scopeopt__name">{s}</span>
                <span className="scopeopt__help">{SCOPE_HELP[s]}</span>
              </span>
            </label>
          ))}
        </div>
      </Field>

      <Field
        label="Wallet allowlist (optional)"
        hint="Restrict this key to specific addresses. Leave blank for all wallets."
      >
        <input
          className="input"
          value={wallets}
          placeholder="0xabc…, 0xdef…"
          onChange={(e) => setWallets(e.target.value)}
        />
      </Field>

      <Field label="Expires">
        <select
          className="select"
          value={expiryDays === null ? 'never' : String(expiryDays)}
          onChange={(e) =>
            setExpiryDays(e.target.value === 'never' ? null : Number(e.target.value))
          }
        >
          {EXPIRY_OPTIONS.map((o) => (
            <option key={o.label} value={o.days === null ? 'never' : String(o.days)}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>

      {error && <div className="notice notice--danger">{error}</div>}
    </Modal>
  );
}

function RevealModal({ created, onClose }: { created: CreatedKey; onClose: () => void }) {
  return (
    <Modal
      title="Save your API key"
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          I’ve saved it
        </Button>
      }
    >
      <div className="notice notice--warn">
        <span>
          This is the only time the full key is shown. Store it somewhere safe now — if you
          lose it, revoke it and create a new one.
        </span>
      </div>
      <div className="reveal">
        <div className="reveal__key">{created.key}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <CopyButton value={created.key} label="Copy key" />
        </div>
      </div>
      <div className="keyrow__meta">
        <span>
          <b>Environment</b> {created.environment}
        </span>
        <span>
          <b>Scopes</b> {created.scopes.join(', ') || 'none'}
        </span>
      </div>
    </Modal>
  );
}

function RevokeModal({
  k,
  onClose,
  onRevoked,
  onError,
}: {
  k: ApiKey;
  onClose: () => void;
  onRevoked: () => void;
  onError: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const revoke = async () => {
    setBusy(true);
    try {
      await apiFetch(`/v1/keys/${k.id}`, { method: 'DELETE' });
      onRevoked();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Could not revoke the key.');
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Revoke key"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={revoke} disabled={busy}>
            {busy ? 'Revoking…' : 'Revoke key'}
          </Button>
        </>
      }
    >
      <p>
        Revoke <b>{k.name}</b>? Any agent using it will immediately lose access. This cannot be
        undone.
      </p>
    </Modal>
  );
}
